import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Truck, CheckCircle2, AlertTriangle, MapPin, Clock, Phone, Ruler, ShieldCheck, ArrowRight, Droplets, Sprout, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
export function DeliveryPrep() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  const stagger = {
    initial: {
      opacity: 0
    },
    whileInView: {
      opacity: 1
    },
    viewport: {
      once: true
    },
    transition: {
      staggerChildren: 0.1
    }
  };
  const item = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5
    }
  };
  return <div className="pt-24 pb-16 bg-[#fdfbf7] dark:bg-[#2e2e2e] min-h-screen" data-id="element-2819" data-mp="2q4zrz7hmwbif">
      {/* Hero Section — gravel photo background with a uniform black
         overlay, same treatment as the rest of the site's photo heroes
         so the white headline stays readable while the material under
         it cues "this page is about delivery prep, on real gravel." */}
      <section className="relative py-20 overflow-hidden border-b border-stone-200 dark:border-white/15" data-id="element-2820" data-mp="cgdldzxd9gaq">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/gukm22ExLHvasq3sgFamZQ/PXL_20260530_192218218~2.jpg')"
      }} aria-hidden="true" data-id="element-2821" data-mp="194s9ixkx2c96" />
        {/* Black overlay — matches the flat opacity Hero.tsx uses on
         mobile, since this page is always center-aligned and doesn't
         need a directional gradient. */}
        <div className="absolute inset-0 bg-black/60" aria-hidden="true" data-id="element-2822" data-mp="3kw5n8en4nhv6" />
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" data-id="element-2823" data-mp="3fvrltfg17664" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2824" data-mp="3hernqeamb4vi">
          
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.5
        }} className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6" data-id="element-2825" data-mp="3mb8elg97bxca">
            Prepare for Your <span className="text-brand-orange" data-id="element-2826" data-mp="2yl5mj05aerdo">Delivery</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto font-medium" data-id="element-2827" data-mp="33f049y1c0wa4">
            Follow this simple guide to ensure a smooth, safe, and successful
            material delivery to your home or job site.
          </motion.p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16" data-id="element-2828" data-mp="212gt3hyhz0l2">
        {/* Before Delivery Day */}
        <motion.section {...fadeIn} data-id="element-2829" data-mp="1w3vuto2t2qz9">
          <div className="flex items-center gap-4 mb-8" data-id="element-2830" data-mp="2a23ysfy9esjx">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/30" data-id="element-2831" data-mp="2a3neo5g071l6">
              <CalendarDays className="w-6 h-6 text-brand-orange" data-id="element-2832" data-mp="262v15003maj3" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-2833" data-mp="2jy6w98i6230j">
              Before Delivery Day
            </h2>
          </div>
          <motion.div variants={stagger} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2834" data-mp="2torugutxh5ix">
            {[{
            icon: <Truck className="w-5 h-5" data-id="element-2835" data-mp="32rj9f4m0ijfb" />,
            text: 'Clear the dump area of vehicles, toys, and obstacles'
          }, {
            icon: <MapPin className="w-5 h-5" data-id="element-2836" data-mp="2xptwfjf8bvol" />,
            text: 'Mark the exact spot where you want material dumped (use a cone, tarp, or flag)'
          }, {
            icon: <ShieldCheck className="w-5 h-5" data-id="element-2837" data-mp="110alvneync76" />,
            text: 'Ensure gate access if fenced and provide gate codes if applicable'
          }, {
            icon: <Ruler className="w-5 h-5" data-id="element-2838" data-mp="19tnojh2tts6g" />,
            text: 'Measure clearance: Trucks are 10ft wide, 12ft tall, and 30ft long'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-2839" data-mp="d57ous6062fm" />,
            text: 'Check for low-hanging overhead wires or tree branches'
          }, {
            icon: <CheckCircle2 className="w-5 h-5" data-id="element-2840" data-mp="m4kyply1gt0v" />,
            text: 'Confirm someone 18+ will be on-site to receive the delivery'
          }].map((point, i) => <motion.div key={i} variants={item} className="flex items-start gap-3 p-4 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm" data-id="element-2841" data-mp="r47fg5d2sref" data-mpi={i}>
                <div className="text-brand-orange shrink-0 mt-0.5" data-id="element-2842" data-mp="22hjlmmwb5m3r">
                  {point.icon}
                </div>
                <p className="text-brand-charcoal dark:text-stone-200 font-medium" data-id="element-2843" data-mp="2cm013f5vl171">
                  {point.text}
                </p>
              </motion.div>)}
          </motion.div>
        </motion.section>

        {/* Day of Delivery */}
        <motion.section {...fadeIn} data-id="element-2844" data-mp="1yv37pm44kfyx">
          <div className="flex items-center gap-4 mb-8" data-id="element-2845" data-mp="1yu1yln6o63ju">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20" data-id="element-2846" data-mp="1tw0f515c027u">
              <Clock className="w-6 h-6 text-brand-orange" data-id="element-2847" data-mp="1ovvvj294mrde" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-2848" data-mp="1jxkp71i1iyzx">
              Day of Delivery
            </h2>
          </div>
          <div className="bg-white dark:bg-[#353535] rounded-2xl border border-stone-200 dark:border-white/15 shadow-sm overflow-hidden" data-id="element-2849" data-mp="1fybu9tb9kv2s">
            <div className="divide-y divide-stone-200 dark:divide-white/15" data-id="element-2850" data-mp="3a9ql6dg0h0k4">
              {[{
              icon: <Phone className="w-6 h-6" data-id="element-2851" data-mp="17n31pyx9eqrd" />,
              title: 'Driver will call',
              desc: 'Expect a call 15-30 minutes before the driver arrives at your location.'
            }, {
              icon: <MapPin className="w-6 h-6" data-id="element-2852" data-mp="ynpqp1thlu4j" />,
              title: 'Point the way',
              desc: 'Direct the driver to your marked dump spot upon arrival.'
            }, {
              icon: <AlertTriangle className="w-6 h-6" data-id="element-2853" data-mp="1qeceqi68nxnp" />,
              title: 'Stay clear',
              desc: 'Keep children, pets, and yourself at a safe distance during the dumping process.'
            }, {
              icon: <CheckCircle2 className="w-6 h-6" data-id="element-2854" data-mp="1he7goymxqbm7" />,
              title: 'Inspect material',
              desc: 'Verify the material is correct before the driver leaves the site.'
            }].map((step, i) => <div key={i} className="p-6 flex items-start gap-4" data-id="element-2855" data-mp="5xpsflthrfan" data-mpi={i}>
                  <div className="w-10 h-10 rounded-full bg-brand-orange/10 dark:bg-brand-orange/20 flex items-center justify-center text-brand-orange shrink-0" data-id="element-2856" data-mp="axc8e30pf9n5">
                    {step.icon}
                  </div>
                  <div data-id="element-2857" data-mp="3uke20bgrqls2">
                    <h3 className="text-lg font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-2858" data-mp="3uj2rlsstifrz">
                      {step.title}
                    </h3>
                    <p className="text-stone-700 dark:text-stone-400 font-medium" data-id="element-2859" data-mp="7feqylyjo89g">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.section>

        {/* Access Tips */}
        <motion.section {...fadeIn} data-id="element-2860" data-mp="3axyb1172k7t6">
          <div className="flex items-center gap-4 mb-8" data-id="element-2861" data-mp="3pkrgleqsdz86">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20" data-id="element-2862" data-mp="3pmcb54d3pi61">
              <Truck className="w-6 h-6 text-brand-orange" data-id="element-2863" data-mp="2x4qhv53cb49d" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-2864" data-mp="2x2ngjowqlopc">
              Access Tips
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2865" data-mp="26ndqf9lhxrim">
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm" data-id="element-2866" data-mp="2bl5gdy2kz3k6">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-2867" data-mp="2gjgrec4m3k3z">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-2868" data-mp="2lkcuwol1x48v" />
                Firm Ground Required
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2869" data-mp="2pkwtzxp6qhxn">
                Fully loaded trucks are extremely heavy. They need firm ground
                or pavement to drive on, not soggy lawns or soft dirt, to avoid
                getting stuck or causing damage.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm" data-id="element-2870" data-mp="3995x32s9o8bx">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-2871" data-mp="3d9zklv1srl4a">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-2872" data-mp="11ogszywfxr5l" />
                Steep Driveways
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2873" data-mp="xk95g51pyh8y">
                Very steep driveways may be unsafe for a dump truck to navigate
                or dump on. In these cases, a street-level dump may be required.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm" data-id="element-2874" data-mp="1lad9gditkj0h">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-2875" data-mp="1gd3i2sbvdxa0">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-2876" data-mp="1bjihcsiw6p74" />
                Cul-de-sacs & Tight Turns
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2877" data-mp="241mjcnwi59sl">
                Large dump trucks need significant turning radius. Ensure there
                is adequate room to maneuver in cul-de-sacs or tight
                neighborhoods.
              </p>
            </div>
            <div className="p-6 bg-brand-orange/10 dark:bg-brand-orange/10 rounded-xl border border-brand-orange/30 shadow-sm flex flex-col justify-center" data-id="element-2878" data-mp="7xt2z6aq9skx">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-2879" data-mp="mw4y38igct9z">
                <Phone className="w-5 h-5 text-brand-orange" data-id="element-2880" data-mp="hxtp0zzzemst" />
                Unsure about access?
              </h3>
              <p className="text-stone-700 dark:text-stone-300 text-sm font-medium mb-3" data-id="element-2881" data-mp="qwf06a34izjk">
                If you have concerns about your delivery location, call us first
                and we can review a photo of your site.
              </p>
              <a href="tel:206-619-3742" className="inline-flex items-center text-brand-orange font-bold hover:underline" data-id="element-2882" data-mp="10njgbd4jcqfz">
                Call (206) 619-3742 <ArrowRight className="w-4 h-4 ml-1" data-id="element-2883" data-mp="1ejn5gs0jy8ll" />
              </a>
            </div>
          </div>
        </motion.section>

        {/* After Delivery */}
        <motion.section {...fadeIn} data-id="element-2884" data-mp="1t9zyht09186p">
          <div className="flex items-center gap-4 mb-8" data-id="element-2885" data-mp="1t6vogf03ri84">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20" data-id="element-2886" data-mp="1y6i22pjuf0hg">
              <Sprout className="w-6 h-6 text-brand-orange" data-id="element-2887" data-mp="1ka4m5kuyj753" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-2888" data-mp="1p5jww3xo4mxa">
              After Delivery
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2889" data-mp="1f971n1mh610f">
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm text-center" data-id="element-2890" data-mp="11hirh3vsdmm3">
              <Clock className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-2891" data-mp="15k5odj7bdxq5" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-2892" data-mp="rjmjum2259sq">
                Spread Quickly
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2893" data-mp="wh4h3f6pq0kw">
                Spread material within 48 hours to avoid settling and compaction
                in the pile.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm text-center" data-id="element-2894" data-mp="2tesbpir1340p">
              <Droplets className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-2895" data-mp="2kgqezfebuxj8" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-2896" data-mp="3h6h9hemmckp1">
                Wet Before Compacting
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2897" data-mp="3d5xd4d9vdl3y">
                Lightly wet gravel before using a plate compactor for maximum
                density and stability.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm text-center" data-id="element-2898" data-mp="336g5vreyyz5v">
              <Sprout className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-2899" data-mp="1rrt07m9briv8" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-2900" data-mp="1rtvvolt8y47c">
                Protect Trees
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-2901" data-mp="1mm5y8mf1nus4">
                When spreading bark or mulch, keep it at least 3 inches away
                from tree trunks to prevent rot.
              </p>
            </div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section {...fadeIn} className="bg-brand-charcoal dark:bg-[#282828] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-2902" data-mp="20evj2niu6tnn">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none" data-id="element-2903" data-mp="25f9vehrh0prh"></div>
          <div className="relative z-10" data-id="element-2904" data-mp="2fabfv0asx3nq">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-4" data-id="element-2905" data-mp="2f90h5ew7qm3h">
              Ready to order?
            </h2>
            <p className="text-stone-300 text-lg mb-8 max-w-xl mx-auto font-medium" data-id="element-2906" data-mp="kwk2pqt9k1nj">
              Now that you know how to prepare, head over to our Materials Hub
              to select your products and schedule your delivery.
            </p>
            <a href="/#materials" className="inline-flex items-center justify-center bg-brand-orange hover:bg-brand-orangeHover text-white px-8 py-4 rounded-xl font-extrabold text-lg transition-all shadow-[0_0_15px_rgba(255,140,50,0.3)] hover:shadow-[0_0_20px_rgba(255,140,50,0.3)]" data-id="element-2907" data-mp="26hgqokj97gxu">
              Browse Materials Hub
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-2908" data-mp="1nouzidjakejl" />
            </a>
          </div>
        </motion.section>
      </div>
    </div>;
}